import { useEffect, useRef, useState } from 'react';
import { Save, X, FolderDown, AlertCircle, Check } from 'lucide-react';
import { tcpFilename } from '../utils/tcpFormat';
import { toast } from '../lib/toast';

// Save-Dialog für den .tcp-Export aus dem AppHeader (Task #40).
//
// Vor Task #40 hat der Speichern-Button die Datei sofort heruntergeladen,
// mit dem aus calculationName abgeleiteten Dateinamen — User hatten
// keine Möglichkeit, Namen oder Speicherort vorab zu prüfen, und ein
// echter Undo ist beim lokalen Dateispeichern ohnehin nicht möglich
// (die Datei liegt dann auf dem Rechner).
//
// Stattdessen: Zwischen-Modal mit editierbarem Dateinamen und — wenn
// der Browser die File System Access API unterstützt (Chrome/Edge) —
// einem "Speichern unter…"-Button, der den nativen Speicherort-Picker
// öffnet. Fallback in allen anderen Browsern: klassischer Download in
// den Downloads-Ordner mit dem hier bestätigten Namen.

const SUPPORTS_FS_PICKER = typeof window !== 'undefined'
  && typeof window.showSaveFilePicker === 'function';

export default function SaveTcpDialog({ open, onClose, defaultName, jsonContent }) {
  // Filename-Draft ohne .tcp-Suffix bearbeiten lassen, dann beim Save
  // wieder anhängen — sauberer für den User als ein Input wo .tcp
  // mitscrollt und versehentlich gelöscht werden kann.
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  // Beim Öffnen Default-Namen frisch übernehmen + Input auswählen,
  // damit Enter den Save sofort auslösen kann.
  useEffect(() => {
    if (!open) return;
    const initial = tcpFilename(defaultName || 'toleranzprojekt').replace(/\.tcp$/, '');
    setDraft(initial);
    setSaving(false);
    // Kurzer Tick warten, damit das Input gerendert ist bevor wir fokussieren.
    const id = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => clearTimeout(id);
  }, [open, defaultName]);

  // Escape schließt, Enter speichert. Window-Level, damit der Listener
  // auch greift, wenn der Fokus außerhalb des Inputs liegt.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        document.getElementById('save-tcp-confirm')?.click();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const safeName = (draft.trim() || 'toleranzprojekt') + '.tcp';

  // Native Speichern-unter-Picker (Chrome/Edge). Liefert dem User den
  // System-Dialog mit Pfadauswahl. Bei Abbruch wirft der Picker
  // AbortError — den behandeln wir als no-op, damit Toast und State
  // sauber bleiben.
  const handleSaveAs = async () => {
    setSaving(true);
    try {
      const handle = await window.showSaveFilePicker({
        suggestedName: safeName,
        types: [{
          description: 'Toleranzkettenrechner-Projekt',
          accept: { 'application/json': ['.tcp'] }
        }],
      });
      const writable = await handle.createWritable();
      await writable.write(jsonContent);
      await writable.close();
      toast.success(`Gespeichert: ${handle.name}`);
      onClose();
    } catch (err) {
      if (err?.name !== 'AbortError') {
        toast.error(`Speichern fehlgeschlagen: ${err.message || 'unbekannter Fehler'}`);
      }
    } finally {
      setSaving(false);
    }
  };

  // Klassischer Download-Pfad: erzeugt einen Blob, triggert eine
  // unsichtbare <a download>-Klick-Aktion. Browser-Default
  // entscheidet, ob ein Speichern-unter-Dialog erscheint oder direkt
  // in Downloads gespeichert wird (Setting in den Browser-Optionen).
  const handleDownload = () => {
    setSaving(true);
    try {
      const blob = new Blob([jsonContent], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = safeName;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Heruntergeladen: ${safeName}`);
      onClose();
    } catch (err) {
      toast.error(`Speichern fehlgeschlagen: ${err.message || 'unbekannter Fehler'}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="save-tcp-title"
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.55)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-surface border border-line-strong shadow-2xl"
        style={{ borderRadius: 'var(--radius-lg)', width: 'min(480px, 92vw)', padding: 20 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <Save size={18} className="text-brand" aria-hidden="true" />
          <h2 id="save-tcp-title" className="text-ink" style={{ fontSize: 16, fontWeight: 700, margin: 0, flex: 1 }}>
            Als .tcp-Datei speichern
          </h2>
          <button
            type="button"
            onClick={onClose}
            title="Abbrechen"
            className="text-ink-3 hover:text-ink"
            style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 4 }}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <label className="text-ink-2" style={{ display: 'block', fontSize: 11.5, fontWeight: 600, marginBottom: 6 }}>
          Dateiname
        </label>
        <div style={{ display: 'flex', alignItems: 'stretch', gap: 0, marginBottom: 12 }}>
          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            // Filesystem-feindliche Zeichen werden vom tcpFilename-Helper
            // beim finalen Schreiben gestrippt; hier lassen wir sie
            // toleranter durch, damit der User bequem Bindestriche,
            // Unterstriche etc. tippen kann.
            placeholder="toleranzprojekt"
            className="num bg-surface-2 border border-line text-ink"
            style={{
              flex: 1, minWidth: 0,
              padding: '8px 10px', fontSize: 13,
              borderRadius: 'var(--radius-md) 0 0 var(--radius-md)',
              borderRight: 'none',
              outline: 'none',
            }}
          />
          <span
            className="text-ink-3 bg-surface border border-line inline-flex items-center"
            style={{
              padding: '0 10px', fontSize: 12, fontWeight: 600,
              borderRadius: '0 var(--radius-md) var(--radius-md) 0',
            }}
          >
            .tcp
          </span>
        </div>

        {/* Browser-Hinweis: falls die File-System-Access-API NICHT
            unterstützt wird, sagen wir explizit dass die Datei in den
            Downloads-Ordner geht — damit der User nicht sucht. Bei
            Unterstützung wird der „Speichern unter…"-Button primär. */}
        <div
          className="bg-surface-2 border border-line text-ink-3"
          style={{ padding: '10px 12px', borderRadius: 6, fontSize: 11, lineHeight: 1.5, display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 16 }}
        >
          <AlertCircle size={12} className="shrink-0 text-ink-4" style={{ marginTop: 2 }} aria-hidden="true" />
          <span>
            {SUPPORTS_FS_PICKER ? (
              <>„Speichern unter…" öffnet einen System-Dialog, mit dem du Pfad und Namen frei wählst.
              „Downloads-Ordner" speichert direkt mit obigem Namen in deinen Standard-Downloads-Ordner.</>
            ) : (
              <>Dein Browser unterstützt keinen Speicherort-Picker - die Datei landet im Standard-Downloads-Ordner.
              Den Namen oben kannst du frei vergeben.</>
            )}
          </span>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="bg-surface text-ink-2 border border-line hover:bg-surface-2 disabled:opacity-50"
            style={{ padding: '8px 14px', fontSize: 12.5, fontWeight: 600, borderRadius: 'var(--radius-md)', cursor: saving ? 'not-allowed' : 'pointer' }}
          >
            Abbrechen
          </button>
          {SUPPORTS_FS_PICKER && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={saving}
              className="bg-surface text-ink-2 border border-line hover:bg-surface-2 disabled:opacity-50 inline-flex items-center gap-1.5"
              style={{ padding: '8px 14px', fontSize: 12.5, fontWeight: 600, borderRadius: 'var(--radius-md)', cursor: saving ? 'not-allowed' : 'pointer' }}
            >
              <FolderDown size={13} aria-hidden="true" />
              Downloads-Ordner
            </button>
          )}
          <button
            id="save-tcp-confirm"
            type="button"
            onClick={SUPPORTS_FS_PICKER ? handleSaveAs : handleDownload}
            disabled={saving || !draft.trim()}
            className="bg-brand text-white border border-brand shadow-md hover:bg-[var(--color-brand-2)] disabled:opacity-50 inline-flex items-center gap-1.5"
            style={{ padding: '8px 14px', fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-md)', cursor: (saving || !draft.trim()) ? 'not-allowed' : 'pointer' }}
          >
            {saving ? <span className="inline-block animate-spin" style={{ width: 12, height: 12, border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%' }} /> : <Check size={13} aria-hidden="true" />}
            {SUPPORTS_FS_PICKER ? 'Speichern unter…' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
